import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type LayoutMode = "auto" | "compact" | "standard" | "expanded";

interface LayoutPreference {
    mode: LayoutMode;
    setMode: (mode: LayoutMode) => void;
    autoHidePanels: boolean;
    setAutoHidePanels: (value: boolean) => void;
    compactOnFocus: boolean;
    setCompactOnFocus: (value: boolean) => void;
}

const STORAGE_KEY = "play-layout-preference";

const getStoredPreferences = (): Partial<LayoutPreference> => {
    if (typeof window === "undefined") return {};

    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

const savePreferences = (prefs: Partial<LayoutPreference>) => {
    if (typeof window === "undefined") return;

    const toSave = {
        mode: prefs.mode,
        autoHidePanels: prefs.autoHidePanels,
        compactOnFocus: prefs.compactOnFocus,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
};

const LayoutPreferenceContext = createContext<LayoutPreference | undefined>(undefined);

export const LayoutPreferenceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [mode, setModeState] = useState<LayoutMode>(() => {
        const stored = getStoredPreferences();
        return stored.mode || "auto";
    });

    const [autoHidePanels, setAutoHidePanelsState] = useState(() => {
        const stored = getStoredPreferences();
        return stored.autoHidePanels ?? true;
    });

    const [compactOnFocus, setCompactOnFocusState] = useState(() => {
        const stored = getStoredPreferences();
        return stored.compactOnFocus ?? true;
    });

    // Save to localStorage when preferences change
    useEffect(() => {
        savePreferences({ mode, autoHidePanels, compactOnFocus });
    }, [mode, autoHidePanels, compactOnFocus]);

    const setMode = (newMode: LayoutMode) => {
        setModeState(newMode);
    };

    const setAutoHidePanels = (value: boolean) => {
        setAutoHidePanelsState(value);
    };

    const setCompactOnFocus = (value: boolean) => {
        setCompactOnFocusState(value);
    };

    const value: LayoutPreference = {
        mode,
        setMode,
        autoHidePanels,
        setAutoHidePanels,
        compactOnFocus,
        setCompactOnFocus,
    };

    return (
        <LayoutPreferenceContext.Provider value={value}>
            {children}
        </LayoutPreferenceContext.Provider>
    );
};

export const useLayoutPreference = (): LayoutPreference => {
    const context = useContext(LayoutPreferenceContext);
    if (!context) {
        throw new Error("useLayoutPreference must be used within a LayoutPreferenceProvider");
    }
    return context;
};